// import {
//   Card,
//   CardContent,
//   CardDescription,
//   CardHeader,
//   CardTitle,
// } from "./ui/card";
// import { Button } from "./ui/button";
// import { Copy, CheckCircle2 } from "lucide-react";
// import { useState } from "react";
// import { toast } from "sonner";
// import ReactMarkdown from "react-markdown";


// export const LinkedInSectionBlock = ({
//   sectionTitle,
//   description,
//   content,
//   educationalNote,
//   characterLimit,
// }) => {
//   const [copied, setCopied] = useState(false);

//   const handleCopy = async () => {
//     try {
//       await navigator.clipboard.writeText(content);
//       setCopied(true);
//       toast.success("Copied to clipboard!");
//       setTimeout(() => setCopied(false), 2000);
//     } catch (error) {
//       toast.error("Failed to copy");
//     }
//   };

//   return (
//     <Card className="w-full border-l-4 border-l-primary">
//       <CardHeader className="p-3 sm:p-6">
//         <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
//           <div className="flex-1">
//             <CardTitle className="flex flex-wrap items-center gap-2 text-lg sm:text-2xl">
//               {sectionTitle}
//               {characterLimit && (
//                 <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-1 rounded">
//                   {characterLimit}
//                 </span>
//               )}
//             </CardTitle>
//             <CardDescription className="text-xs sm:text-sm">
//               {description}
//             </CardDescription>
//           </div>
//           <Button
//             variant="outline"
//             size="sm"
//             onClick={handleCopy}
//             className="w-full sm:w-auto shrink-0"
//           >
//             {copied ? (
//               <>
//                 <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
//                 Copied!
//               </>
//             ) : (
//               <>
//                 <Copy className="h-4 w-4 mr-2" />
//                 Copy
//               </>
//             )}
//           </Button>
//         </div>
//       </CardHeader>
//       <CardContent className="space-y-4 p-3 sm:p-6 pt-0">
//         <div className="bg-muted/50 p-3 sm:p-4 rounded-lg border overflow-x-auto">
//           <div className="prose prose-sm max-w-none dark:prose-invert">
//             <ReactMarkdown
//               components={{
//                 p: ({ children }) => (
//                   <p className="mb-2 last:mb-0 text-xs sm:text-sm leading-relaxed">
//                     {children}
//                   </p>
//                 ),
//                 strong: ({ children }) => (
//                   <strong className="font-semibold text-foreground">
//                     {children}
//                   </strong>
//                 ),
//                 ul: ({ children }) => (
//                   <ul className="list-disc pl-4 space-y-1 my-2">{children}</ul>
//                 ),
//                 li: ({ children }) => (
//                   <li className="text-xs sm:text-sm leading-relaxed">
//                     {children}
//                   </li>
//                 ),
//                 h3: ({ children }) => (
//                   <h3 className="font-semibold text-sm sm:text-base mb-1 mt-2">
//                     {children}
//                   </h3>
//                 ),
//               }}
//             >
//               {content}
//             </ReactMarkdown>
//           </div>
//           <div className="mt-2 text-xs text-muted-foreground">
//             {content.length} characters
//           </div>
//         </div>

//         <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-3 sm:p-4">
//           <div className="flex items-start gap-2">
//             <span className="text-base sm:text-lg shrink-0">💡</span>
//             <div>
//               <h4 className="font-semibold text-xs sm:text-sm mb-1">
//                 Why This Works
//               </h4>
//               <p className="text-xs sm:text-sm text-foreground leading-relaxed">
//                 {educationalNote}
//               </p>
//             </div>
//           </div>
//         </div>
//       </CardContent>
//     </Card>
//   );
// };


import { useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function stripOuterCodeFence(md = "") {
  const text = String(md ?? "")
    .replace(/\r\n/g, "\n")
    .trim();

  // ```markdown\n...\n``` OR ```\n...\n```
  const fence = text.match(/^```(?:markdown|md|text)?\s*\n([\s\S]*?)\n```$/i);

  return fence ? fence[1].trim() : text;
}

function repairDanglingBullets(md = "") {
  const lines = String(md ?? "")
    .replace(/\r\n/g, "\n")
    .split("\n");
  const out = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Convert standalone "•" line into a real bullet marker
    if (/^\s*•\s*$/.test(line)) {
      out.push("-");
      continue;
    }

    // Merge dangling bullet markers with the next non-empty line
    if (/^\s*([-*+])\s*$/.test(line)) {
      let j = i + 1;
      while (j < lines.length && lines[j].trim() === "") j++;

      if (j < lines.length) {
        out.push(`${line.trim()} ${lines[j].trim()}`);
        i = j;
        continue;
      }
    }

    out.push(line);
  }

  return out.join("\n");
}

export const LinkedInSectionBlock = ({
  sectionTitle,
  description,
  content,
  educationalNote,
  characterLimit,
}) => {
  const [copied, setCopied] = useState(false);

  const cleanedContent = useMemo(() => {
    let md = stripOuterCodeFence(content);
    md = repairDanglingBullets(md);
    return md.trim();
  }, [content]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cleanedContent);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Card className="w-full border-l-4 border-l-primary">
      <CardHeader className="p-3 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-lg sm:text-2xl">
              {sectionTitle}
              {characterLimit && (
                <span className="text-xs font-normal text-muted-foreground bg-muted px-2 py-1 rounded">
                  {characterLimit}
                </span>
              )}
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              {description}
            </CardDescription>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            className="w-full sm:w-auto shrink-0"
          >
            {copied ? (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4 p-3 sm:p-6 pt-0">
        <div className="bg-muted/50 p-3 sm:p-4 rounded-lg border overflow-x-auto">
          <div className="markdown-content space-y-4">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: (props) => (
                  <p
                    className="mb-2 last:mb-0 text-xs sm:text-sm leading-relaxed text-foreground"
                    {...props}
                  />
                ),
                strong: (props) => (
                  <strong
                    className="font-semibold text-foreground"
                    {...props}
                  />
                ),
                em: (props) => (
                  <em className="italic text-foreground" {...props} />
                ),
                ul: (props) => (
                  <ul
                    className="list-disc list-inside space-y-1 my-2 text-xs sm:text-sm text-foreground"
                    {...props}
                  />
                ),
                ol: (props) => (
                  <ol
                    className="list-decimal list-inside space-y-1 my-2 text-xs sm:text-sm text-foreground"
                    {...props}
                  />
                ),
                li: (props) => (
                  <li className="leading-relaxed ml-2" {...props} />
                ),
                h1: (props) => (
                  <h1
                    className="font-bold text-lg sm:text-xl mb-2 mt-3"
                    {...props}
                  />
                ),
                h2: (props) => (
                  <h2
                    className="font-semibold text-base sm:text-lg mb-2 mt-3"
                    {...props}
                  />
                ),
                h3: (props) => (
                  <h3
                    className="font-semibold text-sm sm:text-base mb-1 mt-2"
                    {...props}
                  />
                ),
                pre: (props) => (
                  <pre
                    className="bg-muted/60 p-3 rounded-md overflow-x-auto mb-3"
                    {...props}
                  />
                ),
                code: ({ inline, children, ...props }) => {
                  if (inline) {
                    return (
                      <code
                        className="bg-muted/60 px-1.5 py-0.5 rounded text-[11px] sm:text-xs font-mono text-foreground"
                        {...props}
                      >
                        {children}
                      </code>
                    );
                  }
                  return (
                    <code
                      className="text-[11px] sm:text-xs font-mono text-foreground"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                a: (props) => (
                  <a
                    className="text-primary hover:underline font-medium"
                    target="_blank"
                    rel="noreferrer"
                    {...props}
                  />
                ),
                blockquote: (props) => (
                  <blockquote
                    className="border-l-4 border-primary pl-4 italic text-muted-foreground mb-3"
                    {...props}
                  />
                ),
              }}
            >
              {cleanedContent}
            </ReactMarkdown>
          </div>

          <div className="mt-2 text-xs text-muted-foreground">
            {cleanedContent.length} characters
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-lg p-3 sm:p-4">
          <div className="flex items-start gap-2">
            <span className="text-base sm:text-lg shrink-0">💡</span>
            <div>
              <h4 className="font-semibold text-xs sm:text-sm mb-1">
                Why This Works
              </h4>
              <p className="text-xs sm:text-sm text-foreground leading-relaxed">
                {educationalNote}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
